/**
 * Data layer for `admitad-integration`. Thin typed fetch wrappers over
 * /api/v1/admitad-integration. Raw `fetch()` is confined to this file per the
 * dash contract (D6).
 */

export interface CredentialRow {
	id: string;
	admitad_client_id: string;
	admitad_api_key_preview: string;
	status: string;
	created_at: number;
}

export interface ListCredentialsResponse {
	items: CredentialRow[];
}

export interface CreateCredentialBody {
	admitad_client_id: string;
	admitad_api_key: string;
}

export interface SyncStatusResponse {
	feature: string;
	has_credentials: boolean;
	has_offer_mappings: boolean;
	has_postbacks: boolean;
	items: unknown[];
}

const BASE = "/api/v1/admitad-integration";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listCredentials(): Promise<ListCredentialsResponse> {
	const res = await fetch(`${BASE}/credentials`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCredentialsResponse>(res);
}

export async function createCredential(
	body: CreateCredentialBody,
): Promise<{ ok: boolean; item: CredentialRow }> {
	const res = await fetch(`${BASE}/credentials`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ ok: boolean; item: CredentialRow }>(res);
}

export async function getStatus(): Promise<SyncStatusResponse> {
	const res = await fetch(`${BASE}/`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<SyncStatusResponse>(res);
}

/**
 * Validate that an API key matches the expected Partner Network shape.
 * Rule: at least 16 chars, hex/base64-safe characters only.
 */
export function isValidApiKey(key: string): boolean {
	if (typeof key !== "string") return false;
	if (key.length < 16) return false;
	return /^[A-Za-z0-9_\-]+$/.test(key);
}

/**
 * Validate publisher ID (admitad_client_id): non-empty alphanumeric / dash.
 */
export function isValidPublisherId(id: string): boolean {
	if (typeof id !== "string") return false;
	if (id.length === 0) return false;
	return /^[A-Za-z0-9_\-]+$/.test(id);
}

export const admitadIntegrationQueryKeys = {
	all: ["admitad-integration"] as const,
	credentials: () => [...admitadIntegrationQueryKeys.all, "credentials"] as const,
	status: () => [...admitadIntegrationQueryKeys.all, "status"] as const,
};
